import { Box, Grid, Skeleton, Typography } from "@mui/material";
import Title from "../components/global/Title";
import WrapperSection from "../components/global/WrapperSection";
import Header from "../components/global/headers/Header";
import { useEffect, useState } from "react";
import AnimatedSection from "../components/global/AnimatedSection";
import { useProducts } from "../hooks/products/useProducts";
import { useProductsCategories } from "../hooks/products/useProductsCategories";
import { useLocation } from "react-router-dom";
import ItemSquares from "../components/productTractor/ItemSquares";
import { motion, AnimatePresence } from 'framer-motion';
import Lottie from "lottie-react";
import tractorAnimation from "./../assets/tractor.json";
// import ttt from "./../assets/ttt.png";
import { Transition } from "react-transition-group";

const ProductsTractor = () => {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.15, // وقت التأخير بين العناصر
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const [isTractorArrived, setIsTractorArrived] = useState(false);
  const location = useLocation();
  const { catSlug } = location.state || {};
  const [page, setPage] = useState(1);
  const [selectCat, setSelectCat] = useState(catSlug || null);
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    // Detect RTL languages
    setIsRTL(document.documentElement.getAttribute("dir") === "rtl");
  }, []);

  useEffect(() => {
    setSelectCat(catSlug || null);
  }, [catSlug]);

  const {
    products,
    isLoading,
    isFetching: isFetchingProducts,
  } = useProducts();
  const {
    productsCategories,
    isLoading: isLoadingCategories,
    isFetching,
  } = useProductsCategories(page);

  useEffect(() => {
    setPage(1);
  }, [selectCat]);

  const mainCategories = productsCategories?.data
    ?.filter(
      (category) =>
        category.is_main &&
        (category.current_category_product_count > 0 ||
          category.children_products_count > 0)
    )
    .sort((a, b) => parseInt(a.order) - parseInt(b.order));

  const sortedData = mainCategories?.sort((a, b) => a.order - b.order);
  const firstFourProducts = sortedData?.slice(0, 4);

  return (
    <>
      <Box sx={{ overflow: "hidden" }}>
        <Header page_name={products?.pageName} />
        <WrapperSection>
          <Box>
            <Title color="primary" title={products?.title} />
            <Title
              color="primary.bold"
              title={products?.subTitle}
              variant="h4"
            />

            <div className="tractor-container">
              <motion.div
                initial={{ x: isRTL ? -window.innerWidth : window.innerWidth }}
                animate={{ x: 0 }}
                transition={{ duration: 3, ease: "easeInOut" }}
                onAnimationComplete={() => setIsTractorArrived(true)}
              >
                {/* Top icons with exit animation */}
                <AnimatePresence>
                  {!isTractorArrived && (
                    <motion.div
                      key="top-icons"
                      className="tractor-top-icons"
                      exit={{ opacity: 0, scale: 0.5, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      {firstFourProducts?.map((product, index) => (
                        <Box key={index} sx={{ zIndex: 1 }}>
                          <Box
                            component="img"
                            src={product.icon}
                            alt={product.name}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: "50%",
                              objectFit: "cover",
                              mb: 0.5,
                              border: theme => `2px solid ${theme.palette.grey[300]}`,
                              boxShadow: theme => `0px 4px 10px ${theme.palette.primary.main}`,
                            }}
                          />
                        </Box>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                <Lottie
                  animationData={!isTractorArrived && tractorAnimation}
                  loop={!isTractorArrived}
                  autoplay
                  style={{ width: "100%", height: "auto" }}
                />
              </motion.div>
            </div>
            {/* Products Animation */}
            <Transition
              in={isTractorArrived}
              timeout={500}
              mountOnEnter
              unmountOnExit
            >
              {(state) => (
                <Box
                  sx={{
                    width: '650px',
                    display: 'flex',
                    gap: 2,
                    alignItems: {md:'center',xs:'flex-start'},
                    justifyContent: 'center',
                    mx: 'auto',
                    mt: 4,
                    opacity: state === 'entered' ? 1 : 0,
                    transform: state === 'entered' ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.5s ease-in-out, transform 0.5s ease-in-out'
                  }}>
                  {firstFourProducts?.map((product, index) => (
                    <Box key={index}sx={{display:'flex', flexDirection:'column', alignItems:'center', width:{md:'100%',xs:'min-content'}, zIndex: 1}}>
                      <Box
                        component="img"
                        src={product.icon}
                        alt={product.name}
                        sx={{
                          width: { md: 80, xs: 60 },
                          height: { md: 80, xs: 60 },
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                      <Typography key={index} variant='body1' color={'secondary.text'} >{product?.name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Transition>
            {/* Product grid */}
            {isLoadingCategories ||
              isFetching ||
              isLoading ||
              isFetchingProducts ? (
              <Box sx={{ display: "flex", gap: 2 }}>
                {Array.from(new Array(5)).map((_, index) => (
                  <Skeleton
                    animation="wave"
                    key={index}
                    height={250}
                    width={250}
                  />
                ))}
              </Box>
            ) : isTractorArrived ? (
              <Grid
                container
                spacing={2}
                justifyContent={"space-evenly"}
                component={motion.div}
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                {sortedData?.map((product, i) => (
                  <Grid
                    item
                    xl={3}
                    md={3}
                    xs={12}
                    key={product.id}
                    component={motion.div}
                    variants={itemVariants}
                  >
                    <AnimatedSection index={i}>
                      <ItemSquares mainCat={product} />
                    </AnimatedSection>
                  </Grid>
                ))}
              </Grid>
            ) : null}
          </Box>
        </WrapperSection>
      </Box>
    </>
  );
};

export default ProductsTractor;